import { memo, useCallback, useEffect, useState } from "react";
import {
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEqual } from "lodash";

import { Customer, QuickBooksTaxCode } from "../../../models";
import { customerSchema as schema } from "../schemas";
import { useAddCustomer, usePatchCustomer } from "../../../hooks/customer";
import { FormContainer } from "../../layout";
import {
  FormArrayObjectSelect,
  FormAutocomplete,
  FormAutocompleteFreeSolo,
  FormPhoneNumber,
  FormSubmitButton,
  FormSwitch,
  FormTextField,
} from "../../form-components";
import { useChangeDetection } from "../../../contexts/ChangeDetectionContext";
import { useCustomerTiers } from "../../../hooks/customer-tier";
import { useReferralTypes } from "../../../hooks/referral-type";
import { useQuickBooksTaxCodes } from "../../../hooks/quick-books";
import { useAppSettingsContext } from "../../../contexts/AppSettingContext";
import { APPLICATION_SETTING } from "../../../globals/enums";
import {
  formatQuickBooksTaxCodeLabel,
  parseQuickBooksTaxCodeSetting,
} from "../../../utils/quick-books-tax-code";

interface Props {
  customer?: Customer;
  onSubmitted?: (customer: Customer) => void;
}

type CustomerFormValues = Omit<Customer, "customerPayments" | "customerNotes">;

export const CustomerForm = memo((props: Props) => {
  const { customer, onSubmitted } = props;
  const [overrideTax, setOverrideTax] = useState(
    Boolean(customer?.quickbooksTaxCodeId)
  );
  const { getSetting } = useAppSettingsContext();
  const defaultTaxCode = parseQuickBooksTaxCodeSetting(
    getSetting(APPLICATION_SETTING.QB_TAX_CODE_NAME)
  );
  const { setChangeDetection } = useChangeDetection();
  const { mutateAsync: createCustomer } = useAddCustomer();
  const { mutateAsync: patchCustomer } = usePatchCustomer();
  const { data: customerTiers } = useCustomerTiers();
  const { data: referralTypes } = useReferralTypes();
  const {
    data: quickBooksTaxCodes,
    isLoading: quickBooksTaxCodesLoading,
    isFetching: quickBooksTaxCodesFetching,
    refetch: getQuickBooksTaxCodes,
  } = useQuickBooksTaxCodes();
  const {
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { isSubmitting, defaultValues },
  } = useForm<CustomerFormValues>({
    resolver: yupResolver(schema),
    defaultValues: schema.cast(customer ?? ({} as Customer)),
  });
  const setCustomerValue = setValue as (
    name: keyof CustomerFormValues,
    value: CustomerFormValues[keyof CustomerFormValues]
  ) => void;
  const inactive = watch("inactive");
  const currentTaxCode =
    customer?.quickbooksTaxCodeId && customer.quickbooksTaxCodeName
      ? ({
          id: customer.quickbooksTaxCodeId,
          name: customer.quickbooksTaxCodeName,
          rate: customer.quickbooksTaxRate ?? 0,
        } as QuickBooksTaxCode)
      : undefined;

  useEffect(() => {
    const subscription = watch((value, { type }) => {
      type === "change" && setChangeDetection(!isEqual(value, defaultValues));
    });
    return () => subscription.unsubscribe();
  }, [watch, setChangeDetection, defaultValues]);

  const resetForm = useCallback(
    (customer?: Customer) => {
      customer
        ? reset(customer)
        : reset({ firstName: "", lastName: "", email: "", phone: "" });
    },
    [reset]
  );

  useEffect(() => {
    customer ? resetForm(customer) : resetForm();
    setOverrideTax(Boolean(customer?.quickbooksTaxCodeId));
  }, [customer, resetForm]);

  return (
    <FormContainer
      onSubmit={handleSubmit(async (customer) => {
        customer = customer.id
          ? await patchCustomer(customer as Customer)
          : await createCustomer(customer as Customer);
        onSubmitted && onSubmitted(customer as Customer);
        setChangeDetection(false);
      })}
    >
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="firstName"
          label="First Name / Company"
          control={control}
        />
        <FormTextField name="lastName" label="Last Name" control={control} />
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormPhoneNumber name="phone" label="Phone" control={control} />
        <FormTextField name="email" label="Email" control={control} />
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={2}
      >
        {customerTiers && (
          <FormArrayObjectSelect
            name="customerTierId"
            label="Customer Tier"
            data={customerTiers ?? []}
            control={control}
            labelKey="name"
            valueKey="id"
            defaultValue={
              (customer && customer?.customerTier?.id) ?? customerTiers[0].id
            }
            fullWidth
          />
        )}
        <FormTextField
          name="interestTerm"
          label="Interest Term"
          control={control}
          type="number"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">days</InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setOverrideTax(enabled);
                  if (!enabled) {
                    setCustomerValue("quickbooksTaxCodeId", "");
                    setCustomerValue("quickbooksTaxCodeName", "");
                    setCustomerValue("quickbooksTaxRate", null);
                  }
                }}
                checked={overrideTax}
              />
            }
            label={
              overrideTax
                ? "Override QuickBooks Tax Code"
                : `Use System Tax Code${
                    defaultTaxCode
                      ? ` (${formatQuickBooksTaxCodeLabel(defaultTaxCode)})`
                      : ""
                  }`
            }
          />
        </FormGroup>
        {overrideTax && (
          <FormAutocomplete<
            QuickBooksTaxCode,
            CustomerFormValues
          >
            name="quickbooksTaxCodeId"
            label="QuickBooks Tax Code"
            control={control}
            options={
              quickBooksTaxCodes ??
              (currentTaxCode ? [currentTaxCode] : [])
            }
            loading={quickBooksTaxCodesLoading || quickBooksTaxCodesFetching}
            labelKeys={["name", "rate"]}
            separator="-"
            valueKey="id"
            defaultValue={currentTaxCode}
            formatOptionLabel={formatQuickBooksTaxCodeLabel}
            onOpen={() => getQuickBooksTaxCodes()}
            onChange={(taxCode) => {
              setCustomerValue("quickbooksTaxCodeName", taxCode.name);
              setCustomerValue("quickbooksTaxRate", taxCode.rate);
            }}
          />
        )}
        {overrideTax && currentTaxCode && !quickBooksTaxCodes && (
          <Typography variant="caption">
            Current value: {formatQuickBooksTaxCodeLabel(currentTaxCode)}
          </Typography>
        )}
        <FormSwitch
          name="inactive"
          label={inactive ? "Inactive" : "Active"}
          control={control}
        />
      </Stack>
      <Stack>
        <FormAutocompleteFreeSolo
          name="referralType"
          label="Referral Type"
          control={control}
          options={referralTypes?.map((x) => x.name) ?? []}
        />
      </Stack>
      <FormSubmitButton
        text={customer ? "Save Edits" : "Save"}
        disabled={isSubmitting}
      />
    </FormContainer>
  );
});
